/**
 * Panel „Jetzt handeln" der Uebersicht - woertliche Portierung von `index.html:14710-14742`
 * (samt der Helfer `actionFilter`, `index.html:14660`).
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. Die Zaehler an den Filterknoepfen zaehlen ueber `actionRows`, also die UNGEFILTERTE Liste - sie
 *    aendern sich beim Umschalten des Filters NICHT. Die Zahl neben der Ueberschrift
 *    (`.ops-count`) zaehlt dagegen die GEFILTERTE Liste und aendert sich sehr wohl.
 * 2. `info.labels.slice(0, 3)` - hoechstens drei Problem-Chips je Zeile, unabhaengig davon, wie
 *    viele Hinweise `overviewActionInfo` geliefert hat. (Die tote Alt-Fassung
 *    `renderOverviewLegacy` schnitt bei vier ab; die lebende bei drei.)
 * 3. `sub` faellt auf `'Keine Zusatzdaten'` zurueck, wenn Lieferant, Name und Status alle fehlen -
 *    ueber `||` auf der ZUSAMMENGESETZTEN Zeichenkette, nicht je Bestandteil.
 * 4. Der Knopf „N weitere anzeigen" erscheint ab SIEBEN gefilterten Zeilen (`> 6`), sein Text
 *    rechnet aber immer gegen die feste 6 - nicht gegen `overviewActionLimit`.
 * 5. `actionOwner`/`nextActionText` lesen `info.labels`, nicht die Bestellung.
 */
import { fmtDate, NUM } from "../../lib/format.ts";
import { kennungVon } from "../../lib/bestellkennung.ts";
import { actionKind, actionOwner, actionSince, nextActionText } from "../../lib/status.ts";
import type { StatusDefinitionen } from "../../lib/status.ts";
import type { Aktionszeile } from "../../lib/uebersichtAuswertung.ts";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { Ikone } from "../Ikone.tsx";
import { istEinfacherKlick, Verweis } from "../Verweis.tsx";
import { BestellungSplitMarke } from "../SplitMarke.tsx";
import { StockMarke } from "../StockMarke.tsx";

// `Aktionszeile` (eine Zeile aus `actionRows`, index.html:14574-14583) wohnt seit dem KI-Zugang (Stufe 1)
// in `lib/uebersichtAuswertung.ts`, wo die Zeilen berechnet werden.

/** Die vier Zaehler aus `actionCounts` (index.html:14585-14590). */
export interface Aktionszaehler {
  all: number;
  payment: number;
  eta: number;
  master: number;
}

export interface HandlungsbedarfProps {
  /** `filteredActionRows` - bereits nach `effectiveActionType` gefiltert. */
  zeilen: readonly Aktionszeile[];
  zaehler: Aktionszaehler;
  /** Fuer `STATUS_MAP[order.status]?.label` in der Unterzeile. */
  definitionen: StatusDefinitionen;
  /** `effectiveActionType`. */
  aktionsart: string;
  /** `overviewActionLimit` - 6 oder `Infinity`. */
  grenze: number;
  /** `paymentManagerView`. */
  istZahlungsansicht: boolean;
  onArtWaehlen: (wert: string) => void;
  onGrenzeUmschalten: () => void;
  /** `openOverviewAction(id, kind)`, index.html:14494. */
  onOeffnen: (bestellungId: string, art: string) => void;
}

export function Handlungsbedarf({
  zeilen,
  zaehler,
  definitionen,
  aktionsart,
  grenze,
  istZahlungsansicht,
  onArtWaehlen,
  onGrenzeUmschalten,
  onOeffnen,
}: HandlungsbedarfProps) {
  const { ansicht } = useAnsicht();
  // index.html:14594
  const sichtbareZeilen = zeilen.slice(0, grenze);

  // index.html:14660 - `actionFilter(value, label, count)`.
  const filterKnopf = (wert: string, beschriftung: string, anzahl: number) => (
    <button
      key={wert}
      className={`ops-filter ${aktionsart === wert ? "active" : ""}`}
      onClick={() => onArtWaehlen(wert)}
    >
      {beschriftung}
      <b>{NUM.format(anzahl)}</b>
    </button>
  );

  return (
    <section className="ops-panel ops-action-panel">
      <div className="ops-panel-head">
        <div className="ops-panel-title">
          <h2>
            Jetzt handeln <span className="ops-count">{NUM.format(zeilen.length)}</span>
          </h2>
          <p>
            {istZahlungsansicht
              ? "Vom Product Management zur Zahlung freigegebene Bestellungen"
              : "Priorisierte Risiken und der jeweils nächste sinnvolle Schritt"}
          </p>
        </div>
        <div className="ops-filters">
          {istZahlungsansicht
            ? filterKnopf("", "Zahlungen", zaehler.all)
            : [
                filterKnopf("", "Alle", zaehler.all),
                filterKnopf("payment", "Zahlung", zaehler.payment),
                filterKnopf("eta", "Logistik", zaehler.eta),
                filterKnopf("master", "Stammdaten", zaehler.master),
              ]}
        </div>
      </div>
      <div className="ops-actions">
        {sichtbareZeilen.length ? (
          sichtbareZeilen.map(({ order, info }) => {
            const kind = actionKind(info.labels);
            const title = (order.orderNo || order.name || "Ohne Bestellnummer") as string;
            const sub = [
              order.supplier,
              order.name && order.orderNo ? order.name : "",
              definitionen.STATUS_MAP[order.status as string]?.label,
            ]
              .filter(Boolean)
              .join(" · ");
            const since = actionSince(order, info.labels);
            const kennung = kennungVon(order, order.id as string);
            return (
              <Verweis
                key={order.id as string}
                className="tieflink-anker"
                replace
                // Befund 1 der Pruefrunde 3 (Fixrunde 3): `onClick` sitzt HIER, auf dem Anker, nicht
                // auf dem inneren `<button>` - derselbe Grund wie bei `Bestellzeile.tsx`s
                // Bestellnummer-Zelle (dortiger Kopfkommentar, "der Grund, warum es ein ANKER sein
                // muss"). Nur der Anker ist per Tab erreichbar; saesse das `onClick` auf dem Knopf
                // (frueherer Stand dieser Datei, per `tabIndex={-1}` unerreichbar gemacht), erreichte
                // ein Tastaturnutzer `onOeffnen` - und damit `versende({art:"geoeffnet"})`, die SSE-
                // Konflikterkennung - nie.
                onClick={(ereignis) => { if (istEinfacherKlick(ereignis)) onOeffnen(order.id as string, kind); }}
                ziel={{
                  ...ansicht,
                  // `onOeffnen` (App.tsx#uebersichtOeffnen, seit dem 2026-09-01 mit `seiteWechseln:
                  // false` verdrahtet fuer die Uebersichtsseite) wechselt die Seite hier NICHT mehr -
                  // das Anker-Ziel braucht darum kein eigenes `seite: "board"` mehr und uebernimmt sie
                  // einfach aus `...ansicht` (auf dieser Seite immer "overview"). Den Reiter waehlt es
                  // weiterhin selbst nach `art` (`kind === "payment" ? "pay" : kind === "task" ?
                  // "tasks" : "main"`) - das muss weiter die Regel von `onOeffnen` spiegeln, sonst
                  // zeigt der Link auf etwas anderes als der Klick.
                  bestellung: {
                    kennung,
                    tab: kind === "payment" ? "pay" : kind === "task" ? "tasks" : "main",
                    aufgabe: null,
                    dokument: null,
                  },
                }}
              >
                {/* Der Knopf bleibt interaktiv in den Anker verschachtelt (`<a><button>`) - nach dem
                    HTML-Inhaltsmodell unzulaessig, hier aber bewusst NICHT aufgeloest: ein Tagwechsel
                    auf `<div>` braeche die woertliche Paritaet gegen `index.html`
                    (`uebersichtStruktur.test.tsx`), und das waere ein weiterer Schritt vom
                    Bestandsmarkup weg, als der Ankerumbau selbst schon macht - eine Entscheidung des
                    Auftraggebers, nicht des Umsetzers. Der Knopf traegt seit Fixrunde 3 bewusst KEIN
                    eigenes `onClick` mehr - siehe `onClick` am `<Verweis>` oben.

                    `tabIndex={-1}` NACHGETRAGEN (Endabnahme-Nachbesserung, Punkt 3): in Fixrunde 3
                    noch zurueckgenommen, weil das `onClick` damals auf dem Knopf sass und das
                    Attribut den Tastaturweg gerissen haette. Seit `onClick` auf dem Anker sitzt
                    (siehe oben), ist der Knopf ohnehin nur noch Kulisse - `tabIndex={-1}` nimmt ihn
                    aus der Tabreihenfolge und beseitigt den Doppel-Tabstopp, ohne den Tastaturweg
                    (der laeuft ueber den Anker) zu beruehren. */}
                <button className={`ops-action-row ${info.level === 2 ? "urgent" : ""}`} tabIndex={-1}>
                  <span className="ops-priority-dot"></span>
                  <span className="ops-order">
                    <strong>
                      {title}
                      <StockMarke order={order} />
                      <BestellungSplitMarke order={order} klickbar={false} />
                    </strong>
                    <span>{sub || "Keine Zusatzdaten"}</span>
                  </span>
                  <span className="ops-problems">
                    {info.labels.slice(0, 3).map((label, i) => (
                      <span key={i} className={`ops-problem ${label.level === 2 ? "urgent" : ""}`}>
                        {label.text}
                      </span>
                    ))}
                  </span>
                  <span className="ops-next">
                    <small>
                      {actionOwner(info.labels)}
                      {since ? ` · seit ${fmtDate(since)}` : ""}
                    </small>
                    <strong>{nextActionText(info.labels)}</strong>
                  </span>
                  <span className="ops-arrow">
                    <Ikone name="arrow-right" />
                  </span>
                </button>
              </Verweis>
            );
          })
        ) : (
          <div className="ops-empty">Aktuell besteht kein Handlungsbedarf.</div>
        )}
      </div>
      {zeilen.length > 6 ? (
        <button className="ops-panel-more" onClick={onGrenzeUmschalten}>
          {grenze === 6
            ? `${NUM.format(zeilen.length - 6)} weitere anzeigen`
            : "Auf die wichtigsten 6 reduzieren"}
        </button>
      ) : (
        ""
      )}
    </section>
  );
}
